import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  effect,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, interval } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import { finalize, takeUntil } from 'rxjs/operators';

import { CustomResult } from 'src/app/core/api/models';
import { UsersService } from 'src/app/core/api/services';
import { UtilityService } from 'src/app/core/services/utility.service';
import { BaseIndexModule } from 'src/app/pages/shared/base/modules/base-index.module';
import { RegularExpressions } from '../../../../../core/constants/regular-expressions';
import { ApiNotificationService } from '../../../../../core/services/api-notification.service';
import { ModalTitleComponent } from '../../../../../shared/global/modal-title/modal-title.component';
import { AuthMobileService } from './../../../../../core/services/authentication/auth-mobile.service';

@Component({
  selector: 'app-change-mobile',
  templateUrl: './user-change-mobile.component.html',
  styleUrls: ['./user-change-mobile.component.scss'],
  imports: [BaseIndexModule, ModalTitleComponent, ReactiveFormsModule],
})
export class UserChangeMobileComponent implements OnInit {
  //#region properties
  UtilityService = inject(UtilityService);

  mobile: string;
  newMobileNumber: string;
  userId: any;
  authCode: any;
  protected onDestroy$ = new Subject<void>();
  isAuthCodeError: boolean = false;

  source$ = interval(1000);
  timer: number;
  baseTimer: number = 179;
  isRestartTimer = false;

  loading: boolean;

  form: FormGroup;
  isShowStep2: boolean = false;
  timerSubscription: Subscription;

  //#endregion

  constructor(
    private usersService: UsersService,
    public apiNotificationService: ApiNotificationService,
    private cd: ChangeDetectorRef,
    private authMobileService: AuthMobileService,
    private dialogRef: MatDialogRef<UserChangeMobileComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.userId = data?.userId;
    this.mobile = data?.mobile?.mobile;
    this.timer = this.baseTimer;
    effect(() => {
      let lastTimerSubscriptions =
        this.authMobileService.lastTimerSubscriptions();
      if (lastTimerSubscriptions)
        this.timerSubscription = lastTimerSubscriptions;
    });

    effect(() => {
      let lastUserGuid = this.authMobileService.lastUserGuid();
      if (
        lastUserGuid == this.userId &&
        this.mobile == this.authMobileService.lastMobileNumber()
      )
        this.isShowStep2 = true;
    });

    effect(() => {
      let lastMobileNumber = this.authMobileService.lastMobileNumber();
      if (lastMobileNumber && this.isShowStep2) this.mobile = lastMobileNumber;
    });

    effect(() => {
      let timerValue = this.authMobileService.timerValue();
      if (timerValue > 0 && this.timer != 0)
        this.timer = this.baseTimer - timerValue;
    });
  }

  ngOnInit() {
    this.initForm();
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  initForm() {
    this.form = new FormGroup({
      mobile: new FormControl(
        '',
        Validators.pattern(
          RegularExpressions.mobileNumberPrefixTo98EnglishAndPersian
        )
      ),
      oldMobile: new FormControl('', [
        Validators.pattern(RegularExpressions.mobileNumberPrefixTo98),
      ]),
      authCode: new FormControl(''),
    });
  }

  onShowStep2() {
    if (!this.mobile || this.loading) return;
    this.loading = true;
    this.updateUserMobileCreate()
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.onDestroy$)
      )
      .subscribe((result) => {
        if (result.isSucceed) {
          this.isShowStep2 = true;
          this.authMobileService.setLastMobileNumber(this.mobile);
          this.authMobileService.setLastUserGuid(this.userId);
          this.startTimer();
        } else {
          this.apiNotificationService.notifyMessage({ result });
        }
      });
  }

  //#region api

  //send authCode
  updateUserMobileCreate(): Observable<CustomResult> {
    this.mobile = this.UtilityService.getTextWithEnglishNumber(this.mobile);
    return this.usersService.usersUpdateUserMobilePost({
      body: { userId: this.userId, oldMobile: this.mobile },
    });
  }

  //check authCode
  updateUserMobileUpdate() {
    this.newMobileNumber = this.UtilityService.getTextWithEnglishNumber(
      this.newMobileNumber
    );

    this.unSubs();
    this.loading = true;
    this.usersService
      .usersUpdateUserMobilePut({
        body: {
          newMobile: this.newMobileNumber,
          oldMobile: this.mobile,
          userId: this.userId,
          code: this.authCode,
        },
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe((result) => {
        // this.apiNotificationService.notifyMessage({ result });
        if (result.isSucceed) this.onSuccess();
        else this.isAuthCodeError = true;
      });
  }

  //#endregion

  startTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
      this.timer = this.baseTimer;
    }

    if (this.timerSubscription == null) {
      const subscription = this.source$.subscribe((val) => {
        this.authMobileService.setTimerValue(val + 1);
        if (this.timer === 0) {
          subscription.unsubscribe();
          this.timerSubscription.unsubscribe();
          this.timerSubscription = null;
        }
      });
      if (!this.timerSubscription) {
        this.timer = this.baseTimer;
        this.authMobileService.setTimerSubscription(subscription);
      }
    }
  }

  onResendAuthCode() {
    if (this.timer > 0) return;
    this.loading = true;
    this.updateUserMobileCreate()
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.onDestroy$)
      )
      .subscribe((result) => {
        if (result.isSucceed)
          // this.apiNotificationService.notifyMessage({ result });
          this.startTimer();
        else this.apiNotificationService.notifyMessage({ result });
      });
  }

  onSubmit() {
    this.updateUserMobileUpdate();
  }

  onClose() {
    if (this.isShowStep2) this.dialogRef.close(false);
    else this.dialogRef.close(false);

    this.unSubs();
  }

  onSuccess() {
    this.dialogRef.close(this.newMobileNumber);
    this.authMobileService.setLastMobileNumber('');
    this.authMobileService.setLastUserGuid('');
    this.unSubs();
  }

  unSubs() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
